import { Autocomplete } from '@mui/material';
import { FC, SetStateAction, useCallback, useMemo } from 'react';

import { SearchableSelect, SearchableSelectOption, StyledTextField } from 'components';
import { useLookups, useSizeTypes } from 'contexts';
import { Booking, Chassis, Container, Customer, EquipmentType, Genset, UnitStatus } from 'types';

import useConfirm from 'hooks/use-confirm';
import { InboundUnitSearch, OutboundUnitSearch } from '.';
import { useNewTransaction } from '..';
import { AccountSearch } from '../account';
import { BookingInput } from '../booking';
import { RedeliveryNumber } from '../redelivery';
import { EquipmentProps } from './equipment';
import ManufacturedDate from './manufactured-date';

const ContainerColumn: FC<EquipmentProps> = ({ chassisRef, containerRef, gensetRef, required }) => {
  const { chassis, container, genset, transaction, setChassis, setContainer, setGenset } = useNewTransaction();
  const { ConfirmationDialog, getConfirmation } = useConfirm();
  const { unitStatusOptions, unitGradeOptions } = useLookups();
  const { containerSizeTypeEntries } = useSizeTypes();

  const sizeTypeOptions: SearchableSelectOption[] = useMemo(() => {
    return containerSizeTypeEntries.filter(e => e[1].length === 1).map(e => ({ label: e[0], value: e[1][0] }));
  }, [containerSizeTypeEntries]);

  /* memoize booking change handler to avoid booking input rerender */
  const setBooking = useCallback(
    (args?: SetStateAction<Partial<Booking> | undefined | null>) => {
      setContainer(c => new Container({ ...c, booking: typeof args === 'function' ? args(c.booking) : args }));
    },
    [setContainer]
  );

  /* memoize customer change handler to avoid account search rerender */
  const setCustomer = useCallback(
    (customer?: Customer | null) => {
      setContainer(p => new Container({ ...p, customer }));
    },
    [setContainer]
  );

  const setUnit = useCallback(
    (args?: SetStateAction<Container>) => {
      setContainer(p => new Container(typeof args === 'function' ? args(p) : args));
    },
    [setContainer]
  );

  /* prettier-ignore */
  const setSizeType = useCallback(async (sizeType: string) => {
    if (!sizeType.endsWith('TR')) {
      setUnit(({ fmcsaDate, licensePlate, licensePlateExpirationDate, ...prevState }) => {
        return prevState && { ...prevState, sizeType };
      });
    }
    if (!!chassis.unitNumber) {
      const confirmation = await getConfirmation(
        'Replace Chassis?',
        'Transactions can have either a trailer or a chassis.'
      );
      if (!confirmation) return;
    }
    setUnit(
      prevState =>
        prevState && {
          ...prevState,
          fmcsaDate: chassis.fmcsaDate,
          licensePlate: chassis.licensePlate,
          licensePlateExpirationDate: chassis.licensePlateExpirationDate,
          sizeType
        }
    );
    setChassis(new Chassis());
  }, [chassis, getConfirmation, setChassis, setUnit]);

  const validateOutboundContainer = useCallback(
    async (unit: Container | null) => {
      if (!unit) {
        /* reset previously associated units */
        if (chassis.mountedUnits?.some(({ equipmentType }) => equipmentType === EquipmentType.CONTAINER)) {
          setChassis(new Chassis());
        } else if (genset.baseUnit?.equipmentType === EquipmentType.CONTAINER) {
          setGenset(new Genset());
        }
        return;
      }
      if (unit.baseUnit) {
        if (chassis.id && unit.baseUnit.id !== chassis.id) {
          return `${unit.unitNumber} already mounted on ${unit.baseUnit.unitNumber}`;
        } else if (!chassis.id) {
          setChassis(new Chassis(unit.baseUnit));
        } else if (!chassisRef.current?.isValid()) {
          chassisRef.current?.revalidate();
        }
      }
      const mountedGenset = unit.mountedUnits?.[0];
      if (mountedGenset) {
        if (!genset.id) {
          setGenset(new Genset(mountedGenset));
        } else if (genset.id !== mountedGenset.id) {
          return `${mountedGenset.unitNumber} already mounted on ${unit.unitNumber}`;
        } else if (!gensetRef.current?.isValid()) {
          gensetRef.current?.revalidate();
        }
      }
      if (chassis.id && chassis.mountedUnits?.some(({ id }) => id === unit.id)) {
        return; /* container already mounted on current chassis */
      }
    },
    [chassis.id, chassis.mountedUnits, chassisRef, genset.baseUnit, genset.id, gensetRef, setChassis, setGenset]
  );

  return (
    <div className="d-flex flex-column g-inherit">
      <p className="text-primary-dark font-weight-bold">CONTAINER</p>
      <ConfirmationDialog />
      <div>
        {transaction.inbound ? (
          <InboundUnitSearch
            key="container"
            textFieldRef={containerRef}
            required={required}
            setUnit={setUnit}
            unit={container}
            equipmentType={EquipmentType.CONTAINER}
            data-cy="container-inbound-unit-number-search"
          />
        ) : (
          <OutboundUnitSearch
            textFieldRef={containerRef}
            required={required}
            unit={container}
            setUnit={setUnit}
            customValidation={validateOutboundContainer}
            data-cy="container-outbound-unit-number-search"
          />
        )}
      </div>
      <div>
        <SearchableSelect
          containerClass="text-field"
          inputClass={!container.sizeType ? 'blank' : ''}
          id="container-size-type"
          label="SIZE TYPE"
          disabled={!transaction.inbound}
          required={!containerRef?.current?.isBlank()}
          onClear={() => setUnit(p => ({ ...p, sizeType: undefined }))}
          onValueSelected={setSizeType}
          options={sizeTypeOptions}
          value={container.sizeType ?? ''}
          data-cy="container-size-type-input"
        />
      </div>
      <div>
        <AccountSearch
          required={!containerRef?.current?.isBlank()}
          unit={container}
          setCustomer={setCustomer}
          data-cy="container-account-search"
        />
      </div>
      <div>
        <Autocomplete<UnitStatus | ''>
          fullWidth
          autoHighlight
          size="small"
          disabled={!transaction.inbound}
          options={['', ...unitStatusOptions]}
          value={container.unitStatus ?? ''}
          onChange={(_, status) => setContainer(c => new Container({ ...c, unitStatus: status || undefined }))}
          getOptionLabel={status => status ?? ''}
          renderInput={params => (
            <StyledTextField
              {...params}
              required={!containerRef?.current?.isBlank()}
              label="STATUS"
              data-cy="container-unit-status-input"
            />
          )}
          renderOption={(props, status) => (
            <li {...props} key={status ?? ''}>
              {status ?? ''}
            </li>
          )}
        />
      </div>
      <div>
        <SearchableSelect
          containerClass="text-field"
          inputClass={!container.unitGrade ? 'blank' : ''}
          id="container-unit-grade"
          label="UNIT GRADE"
          disabled={!transaction.inbound}
          onClear={() => setUnit(p => ({ ...p, unitGrade: undefined }))}
          onValueSelected={(unitGrade: any) => setUnit(p => p && { ...p, unitGrade: unitGrade || undefined })}
          options={[''].concat(unitGradeOptions).map(value => ({ value, label: value }))}
          value={container.unitGrade ?? ''}
          data-cy="container-unit-grade-input"
        />
      </div>
      <BookingInput
        unit={container}
        setBooking={setBooking}
        required={!transaction.inbound && !containerRef?.current?.isBlank()}
        data-cy="container-booking-search"
      />
      {!!transaction.inbound && (
        <RedeliveryNumber data-cy="container-redelivery-number-input" unit={container} setUnit={setUnit} />
      )}
      <ManufacturedDate unit={container} setUnit={setUnit} />
      <div className="checkboxes d-flex g-2 justify-content-center">
        <label className="font-weight-bold">LOADED</label>
        <input
          type="checkbox"
          checked={!!container.loaded}
          disabled={!transaction.inbound}
          onChange={e => setContainer(c => new Container({ ...c, loaded: e.target.checked }))}
          data-cy="container-loaded-checkbox"
        />
      </div>
    </div>
  );
};

export default ContainerColumn;
